import { useMemo, useState, useEffect } from 'react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import type { StationPriceHistory, TimeRange, FuelType } from '@fuelfinder/shared'

interface PriceHistoryChartProps {
  history: StationPriceHistory
  timeRange: TimeRange
  onTimeRangeChange: (range: TimeRange) => void
  isLoading: boolean
}

const TIME_RANGES: { value: TimeRange; label: string }[] = [
  { value: '30d', label: '30d' },
  { value: '3m', label: '3m' },
  { value: '1y', label: '1y' },
  { value: 'max', label: 'Max' },
]

const FUEL_COLORS: Partial<Record<FuelType, string>> = {
  E10: '#16a34a',
  B7: '#2563eb',
  E5: '#d97706',
  SDV: '#7c3aed',
}

const FUEL_LABELS: Partial<Record<FuelType, string>> = {
  E10: 'Petrol',
  B7: 'Diesel',
  E5: 'Super',
  SDV: 'Premium',
}

function formatDateLabel(dateStr: string, range: TimeRange): string {
  const date = new Date(dateStr)
  if (range === '30d') {
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
  }
  return date.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' })
}

function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: Array<{ color: string; name: string; value: number | null }>
  label?: string
}) {
  if (!active || !payload || !label) return null

  const date = new Date(label)
  const dateStr = date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm">
      <p className="text-slate-500 dark:text-slate-400 mb-1">{dateStr}</p>
      {payload.map((entry) =>
        entry.value != null ? (
          <p key={entry.name} style={{ color: entry.color }} className="font-semibold">
            {entry.name}: {entry.value.toFixed(1)}p
          </p>
        ) : null
      )}
    </div>
  )
}

export function PriceHistoryChart({
  history,
  timeRange,
  onTimeRangeChange,
  isLoading,
}: PriceHistoryChartProps) {
  const { chartData, activeFuelTypes, yDomain } = useMemo(() => {
    if (!history || history.dates.length === 0) {
      return { chartData: [], activeFuelTypes: [] as FuelType[], yDomain: [0, 200] as [number, number] }
    }

    // Dates are newest-first, reverse for the chart (left=oldest, right=newest)
    const reversedDates = [...history.dates].reverse()

    const fuelTypes = (['E10', 'B7', 'E5', 'SDV'] as FuelType[]).filter((ft) => {
      const arr = history.prices[ft]
      return arr && arr.some((v) => v !== null)
    })

    let minPrice = Infinity
    let maxPrice = -Infinity

    const data = reversedDates.map((date, i) => {
      const reversedIndex = history.dates.length - 1 - i
      const point: Record<string, string | number | null> = { date }

      for (const ft of fuelTypes) {
        const arr = history.prices[ft]
        const val = arr ? arr[reversedIndex] ?? null : null
        point[ft] = val
        if (val !== null) {
          minPrice = Math.min(minPrice, val)
          maxPrice = Math.max(maxPrice, val)
        }
      }

      return point
    })

    const padding = Math.max((maxPrice - minPrice) * 0.1, 2)
    const domain: [number, number] = [
      Math.floor(minPrice - padding),
      Math.ceil(maxPrice + padding),
    ]

    return { chartData: data, activeFuelTypes: fuelTypes, yDomain: domain }
  }, [history])

  const hasData = chartData.length > 0

  // Delay rendering the chart briefly so the container has proper dimensions
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 100)
    return () => clearTimeout(timer)
  }, [])

  return (
    <div className="py-4 border-b border-slate-200 dark:border-slate-700">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-600 dark:text-slate-300">
          Price History
        </h3>
        <div className="flex gap-1">
          {TIME_RANGES.map((tr) => (
            <button
              key={tr.value}
              onClick={() => onTimeRangeChange(tr.value)}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                timeRange === tr.value
                  ? 'bg-green-600 text-white'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
              }`}
            >
              {tr.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="h-40 flex items-center justify-center text-slate-400">
          Loading history...
        </div>
      ) : !hasData ? (
        <div className="h-40 flex items-center justify-center text-slate-400 text-sm">
          No price history available yet
        </div>
      ) : !mounted ? (
        <div className="h-40" />
      ) : (
        <>
          <div style={{ width: '100%', height: 160 }}>
            <ResponsiveContainer width="100%" height={160} minWidth={0}>
              <LineChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: -15 }}>
                <XAxis
                  dataKey="date"
                  tickFormatter={(d) => formatDateLabel(d, timeRange)}
                  tick={{ fontSize: 10, fill: '#94a3b8' }}
                  tickLine={false}
                  axisLine={false}
                  minTickGap={30}
                />
                <YAxis
                  domain={yDomain}
                  tickFormatter={(v) => `${v}p`}
                  tick={{ fontSize: 10, fill: '#94a3b8' }}
                  tickLine={false}
                  axisLine={false}
                  width={45}
                />
                <Tooltip content={<CustomTooltip />} />
                {activeFuelTypes.map((ft) => (
                  <Line
                    key={ft}
                    type="monotone"
                    dataKey={ft}
                    stroke={FUEL_COLORS[ft]}
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                    name={FUEL_LABELS[ft]}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="flex gap-4 mt-2 justify-center">
            {activeFuelTypes.map((ft) => (
              <div key={ft} className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                <span
                  className="w-3 h-0.5 rounded-full inline-block"
                  style={{ backgroundColor: FUEL_COLORS[ft] }}
                />
                {FUEL_LABELS[ft]}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
